import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { AppShell } from './components/layout/AppShell';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { DashboardPage } from './pages/DashboardPage';
import { CompanionPage } from './pages/CompanionPage';
import { HealthPage } from './pages/HealthPage';
import { BusinessesPage } from './pages/BusinessesPage';
import { BusinessDetailPage } from './pages/BusinessDetailPage';
import { AgentsPage } from './pages/AgentsPage';
import { AgentDetailPage } from './pages/AgentDetailPage';
import { SkillDetailPage } from './pages/SkillDetailPage';
import { SquadDetailPage } from './pages/SquadDetailPage';
import { MCPipelinesPage } from './pages/MCPipelinesPage';
import { PipelineDetailPage } from './pages/PipelineDetailPage';
import { PipelineBoardPage } from './pages/PipelineBoardPage';
import { SessionsPage } from './pages/SessionsPage';
import { PullRequestsPage } from './pages/PullRequestsPage';
import { LoopsPage } from './pages/LoopsPage';
import { LoopDetailPage } from './pages/LoopDetailPage';
import { LlmAccountsPage } from './pages/settings/LlmAccountsPage';
import { CompanionsPage } from './pages/settings/CompanionsPage';
import { McpPage } from './pages/settings/McpPage';
import { ConnectorsPage } from './pages/settings/ConnectorsPage';
import { NotificationsPage } from './pages/settings/NotificationsPage';
import { IntegrationCallbackPage } from './pages/IntegrationCallbackPage';
import { AdminLlmResiliencePage } from './pages/AdminLlmResiliencePage';

// Mission Control pages
import { MCTasksPage } from './pages/mc/MCTasksPage';
import { MCDemandsPage } from './pages/mc/MCDemandsPage';
import { MCActivityPage } from './pages/mc/MCActivityPage';

// Admin & Runtime
import { AdminPromotionsPage } from './pages/AdminPromotionsPage';
import { AdminSlashCommandsPage } from './pages/AdminSlashCommandsPage';
import { AdminSlashExecutionsPage } from './pages/AdminSlashExecutionsPage';
import AdminHandlersPage from './pages/AdminHandlersPage';
import AdminRoutesPage from './pages/AdminRoutesPage';
// D4 (2026-05-04): AdminToolsPage — tools como entidade canônica
import AdminToolsPage from './pages/AdminToolsPage';
import { RuntimeNodesPage } from './pages/RuntimeNodesPage';

// Multi-provider v5 (DEM-08)
import { ModelsCatalogPage } from './pages/ModelsCatalogPage';
import { AgentInstantiateWizardPage } from './pages/AgentInstantiateWizardPage';

const DesignSystemPage = React.lazy(() => import('./pages/DesignSystemPage').then(m => ({ default: m.DesignSystemPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-raios-bg">
      <div className="flex flex-col items-center gap-4">
        <img src="/raios-bolt.png" alt="" aria-hidden="true" className="w-10 h-10 animate-pulse" />
        <p className="text-raios-muted text-sm">Carregando RAIOS...</p>
      </div>
    </div>
  );
}

/**
 * /settings → /settings/llm preservando a query string — o callback OAuth
 * (Anthropic/OpenAI) historicamente volta pra /settings?llm=connected e o
 * feedback de sucesso precisa sobreviver ao redirect (§6.10 / LLM-04).
 */
function SettingsRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/settings/llm', search: location.search }} replace />;
}

function ProtectedRoutes() {
  const { user, loading, onboarding } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  // Wait for onboarding status to load before deciding
  if (onboarding === null) return <LoadingScreen />;

  // If onboarding is not completed, force redirect to /onboarding
  if (!onboarding.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }

  return <AppShell />;
}

function OnboardingGuard() {
  const { user, loading, onboarding } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  // If already completed onboarding, go to dashboard
  if (onboarding?.onboarding_completed) {
    return <Navigate to="/" replace />;
  }

  return <OnboardingPage />;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/" replace /> : <LoginPage />}
      />
      {/* DEM-2044: recuperação de senha — rotas públicas (sem auth).
          /forgot-password dispara resetPasswordForEmail; /reset-password recebe
          o token do email (PASSWORD_RECOVERY) e grava a nova senha via updateUser.
          Redirect das telas é configurado em produção via migration do Supabase
          Auth (Site URL=https://raios.app + Redirect URLs allowlist). */}
      <Route
        path="/forgot-password"
        element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/onboarding" element={<OnboardingGuard />} />
      <Route path="/settings/integrations" element={<IntegrationCallbackPage />} />
      <Route element={<ProtectedRoutes />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/companion" element={<CompanionPage />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="/kanban" element={<MCTasksPage />} />
        <Route path="/pull-requests" element={<PullRequestsPage />} />
        <Route path="/businesses" element={<BusinessesPage />} />
        <Route path="/businesses/:id" element={<BusinessDetailPage />} />
        <Route path="/loops" element={<LoopsPage />} />
        <Route path="/loops/:id" element={<LoopDetailPage />} />
        <Route path="/agents" element={<AgentsPage />} />
        {/* Wizard de criação de agente/companion MORTO — criação é via modal de escopo. */}
        <Route path="/agents/:id/instantiate" element={<AgentInstantiateWizardPage />} />
        <Route path="/agents/:id" element={<AgentDetailPage />} />
        <Route path="/models" element={<ModelsCatalogPage />} />
        {/* DEM-1870: /playground MORTO no frontend (sidebar entry + Route + página deletados).
            Backend ephemeral (ephemeral.routes.ts, ephemeral_executions, KindSchema,
            ArchetypeEnum, dispatcher @ephemeral-runner) PERMANECE intacto —
            MCActivityPage e activity-routes.test.ts dependem. */}
        {/* SKL-A: rota ÚNICA com :id opcional — abrir/fechar a modal de detalhe
            NÃO remonta a SkillsPage (antes eram 2 rotas → remount + refetch geral). */}
        <Route path="/skills/:id?" element={<SkillDetailPage />} />
        {/* Wizards de criação de skill MORTOS — criação é via modal de escopo (Criador). */}
        {/* SQUADS — quarto ativo. Rota ÚNICA com :id opcional (mesma decisão
            de SKL-A): abrir/fechar o detalhe não remonta a listagem. Criação é
            via Companion (`create_squad`), então não há wizard aqui. */}
        <Route path="/squads/:id?" element={<SquadDetailPage />} />
        {/* PR-P5 (raios-import-plan v1.7) — Pipelines */}
        <Route path="/pipelines" element={<MCPipelinesPage />} />
        {/* Wizards de criação de pipeline MORTOS — criação é via modal de escopo (Criador). */}
        <Route path="/pipelines/:id" element={<PipelineDetailPage />} />
        <Route path="/pipelines/:id/board" element={<PipelineBoardPage />} />
        {/* Rota antiga /pipeline-runs/:id apontava pra PipelineRunDetailPanel
            (Kanban interno por etapa) — duplicava o Kanban-by-step do
            PipelineBoardPage. Removida em P1.7: detalhe da run agora abre via
            PipelineRunDetailSheet sobre o board (sem trocar de tela). Redirect
            preserva links antigos. */}
        <Route path="/pipeline-runs/:id" element={<Navigate to="/pipelines" replace />} />
        {/* Reforma UX 2026-06-10: Configurações explodida em 3 telas dedicadas
            (§3.2 — rotas dedicadas). /settings vira redirect preservando query. */}
        <Route path="/settings" element={<SettingsRedirect />} />
        <Route path="/settings/llm" element={<LlmAccountsPage />} />
        <Route path="/llm-resilience" element={<AdminLlmResiliencePage mode="tenant" />} />
        <Route path="/settings/companions" element={<CompanionsPage />} />
        <Route path="/settings/mcp" element={<McpPage />} />
        {/* DEM-2120 (Conectores F2) — Marketplace de conectores no nível tenant.
            Detalhe do conector via :slug opcional (abre painel sem remount). */}
        <Route path="/settings/connectors/:slug?" element={<ConnectorsPage />} />
        <Route path="/settings/notifications" element={<NotificationsPage />} />
        <Route path="/health" element={<HealthPage />} />
        <Route path="/design-system" element={
          <Suspense fallback={<LoadingScreen />}>
            <DesignSystemPage />
          </Suspense>
        } />
        {/* Mission Control */}
        {/* /kanban é a rota canônica de tasks; /mc/tasks fica como redirect pra
            preservar links antigos sem manter código duplicado. */}
        <Route path="/mc/tasks" element={<Navigate to="/kanban" replace />} />
        <Route path="/mc/demands" element={<MCDemandsPage />} />
        <Route path="/mc/activity" element={<MCActivityPage />} />
        {/* DEM-1870: /mc/health absorvido por /health (merge System+Operational em tabs).
            Mesmo padrão do redirect /mc/tasks → /kanban. */}
        <Route path="/mc/health" element={<Navigate to="/health" replace />} />
        {/* Admin & Runtime */}
        <Route path="/admin/promotions" element={<AdminPromotionsPage />} />
        <Route path="/admin/slash-commands" element={<AdminSlashCommandsPage />} />
        <Route path="/admin/slash-executions" element={<AdminSlashExecutionsPage />} />
        <Route path="/admin/handlers" element={<AdminHandlersPage />} />
        <Route path="/admin/routes" element={<AdminRoutesPage />} />
        {/* /admin/wizards MORTO — não existe wizard no sistema. */}
        {/* F6.1 (2026-05-22) — render dinâmico schema-driven pros 3 wizards
            canonical (create-pipeline, create-skill, create-agent) + qualquer
            outro registrado em wizards table. */}
        {/* /wizards/:slug MORTO — não existe wizard no sistema. */}
        <Route path="/admin/tools" element={<AdminToolsPage />} />
        <Route path="/admin/llm-resilience" element={<AdminLlmResiliencePage mode="admin" />} />
        <Route path="/runtime/nodes" element={<RuntimeNodesPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
